import * as monaco from 'monaco-editor/esm/vs/editor/editor.api.js';
import { Notify } from 'src/notifications';
import { Ref, onBeforeUnmount, ref, watch } from 'vue';
import { onMounted } from 'vue';
import {
    tsCompilerOptions,
} from '@redviking/argonaut-util/src/argo-lang/parser';

export type CustomEditorOptions = Omit<monaco.editor.IStandaloneEditorConstructionOptions, 'model'>;

// as far as I can tell, these can only be global.
// parts of this app expect these specific settings.
// if we need to ever change them elsewhere, we'll need to work this into `useMonacoEditor` and `validateScript`.
monaco.languages.typescript.javascriptDefaults.setDiagnosticsOptions({
    noSemanticValidation: false,
    noSyntaxValidation: false,
});
monaco.languages.typescript.javascriptDefaults.setCompilerOptions(tsCompilerOptions as monaco.languages.typescript.CompilerOptions);

export function setExtraLibs (extraLibs: { content: string, filePath: string }[]) {
    monaco.languages.typescript.javascriptDefaults.setExtraLibs(extraLibs);
}

/**
 * there can only be one monaco editor instance at a time.
 * https://github.com/microsoft/monaco-editor/issues/2976
 */
let instanceCounter = 0;

export function useMonacoEditor (opts: {
    content: Ref<string>;
    strictReadonly?: boolean;
    readOnlyRef: Ref<boolean>;
    textModel: monaco.editor.ITextModel;
    editorOptions?: CustomEditorOptions;
    editorElementRef: Ref<HTMLElement | null>;
    onKeyDown?: (e: monaco.IKeyboardEvent) => void;
}): {
    editor: Ref<monaco.editor.IStandaloneCodeEditor | null>;
} {
    onMounted(() => {
        if (!opts.strictReadonly) {
            if (instanceCounter > 0) {
                Notify.error(new Error('Only one monaco editor instance can be allowed at a time'));
            }
            instanceCounter++;
        }
    });

    const editor = ref<monaco.editor.IStandaloneCodeEditor | null>(null);
    watch(opts.editorElementRef, editorElement => {
        if (editor.value) {
            editor.value.dispose();
            editor.value = null;
        }

        if (!editorElement) {
            return;
        }

        editor.value = monaco.editor.create(editorElement, {
            model: opts.textModel,
            showDeprecated: false,
            automaticLayout: true,
            quickSuggestions: false,
            fixedOverflowWidgets: true,
            readOnly: opts.readOnlyRef.value || opts.strictReadonly,
            minimap: {
                enabled: false,
            },
            suggest: {
                showUnits: false,
                showUsers: false,
                showFields: true,
                showWords: false,
                showFiles: false,
                showEnums: false,
                showColors: false,
                showValues: false,
                showEvents: false,
                showIssues: false,
                showStructs: false,
                showModules: false,
                showClasses: false,
                showFolders: false,
                showKeywords: false,
                showFunctions: true,
                showOperators: false,
                showVariables: false,
                showConstants: false,
                showDeprecated: false,
                showReferences: false,
                showEnumMembers: false,
                showConstructors: false,
                showTypeParameters: false,
            },
            ...opts.editorOptions,
        });

        editor.value.onDidBlurEditorText(() => {
            if (editor.value) {
                opts.content.value = editor.value.getValue() || '';
            }
        });

        editor.value.onKeyDown(e => {
            if (opts.onKeyDown) {
                opts.onKeyDown(e);
            }
        });
    }, { immediate: true });

    watch(opts.content, newContent => {
        opts.textModel.setValue(newContent);
    }, { immediate: true });

    watch(opts.readOnlyRef, readOnly => {
        if (!editor.value) {
            return;
        }
        editor.value.updateOptions({ readOnly });
    }, { immediate: true });

    onBeforeUnmount(() => {
        if (!opts.strictReadonly) {
            instanceCounter--;
        }
        if (editor.value) {
            editor.value.dispose();
            editor.value = null;
        }
    });

    return {
        editor,
    };
}
